import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RoutesConst } from '@carm/shared/constants/const-routes';
import { AuthService, UserToken } from '@carm/_auth/spartacus/core/auth';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { UserService } from '../access-control-mgt/facade/user.service';
import { IAcl, Role, RoleTypes } from '../access-control-mgt/models';
import { AccessUserService } from '../access-control-mgt/services';
import { AccessControlService } from '../access-control-mgt/services/access-control.service';
import { CarmRouter } from '../carm-router/services/carm-router.service';
import { RoleTypePermissionService } from '../role-type-permissions/services';
import { StaffAccessRequestsService } from '@carm/_core/staff-access-requests/services/staff-access-requests.service';
import { UrlTrackerService } from '@carm/_core/guards/url-tracker.service';

@Injectable()
export class AuthRouteGuard implements CanActivate {
  private previousUrl: string;

  constructor(
    protected authService: AuthService,
    protected userService: UserService,
    protected roleTypePermissionService: RoleTypePermissionService,
    protected accessControlService: AccessControlService,
    protected accessUserService: AccessUserService,
    protected staffAccessRequestsService: StaffAccessRequestsService,
    protected router: CarmRouter,
    private urlTrackerService: UrlTrackerService
  ) {}

  /**
   * Auth Guard is allow after verify four different things
   *  1- Verify users should be logged in - by checking access token
   *  2- Verify users has profile - if not then redirect to UserProfile creation page
   *  3- Verify users has roles - if not then redirect to 1st time setup page
   *  4- Apply ACL and personalization rules if exist on requested route
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    /** Get current URL, need for safe guard during enrolment ***/
    const currentUrl = this.urlTrackerService.getCurrentUrl();

      const personalization: IAcl[] = route &&
                                      route.data &&
                                      route.data.acl
                                      ? route.data.acl
                                      : null;
      const isProfilePage = state.url.endsWith('accounts-and-profiles/personal-profile');
      const isFirstTimePage = state.url.endsWith('accounts-and-profiles/first-time-setup');
      const urlNativeBn9 = route.params['nativeBn9'];
      const urlBn15 = route.params['bn15'];
      const isInvalidUrl = (urlNativeBn9 === 'undefined' || urlBn15 === 'undefined') && !isProfilePage;

      if (isInvalidUrl) {
        console.error('error: \'undefined\' in url: ' + state.url)
        return this.router.carmNavigate([RoutesConst.error404]);
      }

      return this.isUserTokenExist().pipe(
            switchMap( (tokenExist: boolean) => {
              if ( tokenExist ) {
                return this.isUserProfileExist(isProfilePage);
              } else {
                return of(tokenExist);
              }
            }),
            switchMap((isProfileExist: boolean) => {
              if (isProfileExist) {
                return this.isOrphanRole();
              }
              return of(true);
            }),
            switchMap((isOrphanRole: boolean) => {
              if (!isOrphanRole) {
                return this.checkUserLinkedBusiness(isProfilePage);
              } else {
                if(currentUrl !== undefined) {
                  if (currentUrl.endsWith('certification') || currentUrl.endsWith('business-activity') || currentUrl.endsWith('business-contacts')) {
                    return of(false);
                  } else {
                    return this.checkUserPendingRequest(isFirstTimePage, isProfilePage);
                  }
                } else {
                  return this.checkUserPendingRequest(isFirstTimePage, isProfilePage);
                }
              }
            }),
            switchMap((isLinkBusinessExist: boolean) => {
                if (isLinkBusinessExist && !isProfilePage) {
                  return this.checkRole(urlNativeBn9, urlBn15, personalization);
                } else if (isProfilePage) {
                  return of(true);
                }
                return this.checkOrphanAllowed();
              },
            ),
      );
  }

  checkOrphanAllowed(): Observable<boolean> {
    return this.getPermissions().pipe(
      switchMap((permissions) => {
        const isAllowed = this.accessControlService.isAclOrphanAllowedResource(permissions);
        if (!isAllowed) {
          this.router.carmNavigate([RoutesConst.AccountProfile_FirstTimeSetup]);
          return of(false);
        }
        return of(isAllowed);
      }));
  }

  isOrphanRole(): Observable<boolean> {
    return this.userService.getUserInformation()
    .pipe(
      take(1),
      map(userInfo =>
        (userInfo?.role?.lastUsedRole?.roleType === RoleTypes.Orphan && userInfo?.role?.roles?.length === 0)
      )
    );
  }

  /**
   * Determines whether user profile exist if not then redirect to user profile creation page
   * @returns user profile exist
   */
  isUserProfileExist(isProfilePage: boolean): Observable<boolean> {

    const currentUser = this.userService.getUserSync();

    const getUserInformation$ = currentUser ? this.userService.getUserInformation() : this.accessUserService.getUserRoles(); // use ngrx state, fallback on api call

    return getUserInformation$
    .pipe(
      filter((data) => data.user !== null ),
      take(1),
      map((userInfo) => {
          if ( !userInfo.user.isPrivacyTermsConditionsAccepted) {
            console.log('isPrivacyTermsConditionsAccepted FALSE, redirecting to AcceptTerms'); // DEBUG - DELETE ME SOON
            this.userService.clearUserInformation();
            this.router.carmNavigate([RoutesConst.AcceptTerm]);
            return false;
          }
          if (userInfo.user.isDefaultAddressExist) {
            return true;
          } else {
            if (!isProfilePage) {
              this.router.carmNavigate([RoutesConst.AccountProfile_PersonalProfile]);
              return false;
            } else {
              return true;
            }
          }
        },
    ));
  }

  /**
   * Determines whether user token exist, if not then redirect to login page
   * @returns user token exist
   */
  isUserTokenExist(): Observable<boolean> {
    return this.authService.getUserToken().pipe(
      map((token: UserToken) => {
        if (!token.access_token) {
          this.router.carmNavigate([RoutesConst.Login]);
          return false;
        } else {
          return !!token.access_token;
        }
      }), take(1));
  }

  /**
   * Checks role exist, if not then redirect to 1st time setuppage
   * @param personalization
   * @returns role exist or not
   */
  checkRole(nativeBn9: string, bn15: string, personalization: IAcl[]): Observable<boolean> {
      return this.userService.getNativeRoles()
      .pipe(
        filter((nroles) => !!nroles && nroles.length > 0), // wait until native roles loaded
        take(1),
        withLatestFrom(this.userService.getUserInformation()),
        switchMap(([nativeRoles, userInfo]) => combineLatest([of(nativeRoles),
           of(userInfo),
           this.getPermissions(),
           this.getNativePermissions(nativeRoles)
          ])),
        map( ([_, userInfo]) => {
          if (userInfo && (userInfo.role === null || userInfo.role.business === null || userInfo.role.roles.length === 0) ) {
            this.router.carmNavigate([RoutesConst.AccountProfile_FirstTimeSetup]);
            return false;
          } else {
            const isAllowed = this.aclVerification(personalization);
            if (!isAllowed) {
              console.log('access denied - redirecting to dashboard');
              if(nativeBn9 && bn15){
                this.router.carmNavigateByCustomBusinessNumbers(nativeBn9, bn15, [RoutesConst.Dashboard]); // redirect to dashboard instead of blank page (to reconfirm with business)
              }
              return false;
            }
            return isAllowed;
          }
        },
        ));
  }

  getPermissions(){
   return this.roleTypePermissionService.getPermissions()
     .pipe(
       filter(per=> !!per && per.length > 0),
       take(1)
      )
  }

  getNativePermissions(nativeRoles: Role[]){
    return this.roleTypePermissionService.getAllNativeBusinessPermissions()
     .pipe(
       filter(per=> !!per && per.length > 0),
       take(1)
      )
  }

  /**
   * Checks user already have linked business
   * @param isProfilePage
   * @returns user linked business
   */
  checkUserLinkedBusiness(isProfilePage: boolean): Observable<boolean> {
    return this.userService.getUserInformation()
    .pipe(
      filter((data) => {
          return !!data || data.role === null;
      }   ), // data === null means user doesn't have any roles associated
      take(1),
      map( (userInfo) => {
        if (isProfilePage) { // in case of UserProfile Page no need to test role
          return true;
        }
        if (userInfo && (userInfo.role === null || userInfo.role.business === null || userInfo.role.roles.length === 0) ) {
            this.router.carmNavigate([RoutesConst.AccountProfile_FirstTimeSetup]);
            return false;
        } else {
            return true;
        }
      },
      ));
  }

  /**
   * Checks if orphan user has pending request and redirects if necessary
   * @param isProfilePage
   * @param isFirstTimePage
   * @returns Observable<boolean>
   */
  checkUserPendingRequest(isFirstTimePage: boolean, isProfilePage: boolean): Observable<boolean> {
    return this.staffAccessRequestsService.getUserRequest()
    .pipe(
      filter((data) => {
        return !!data || data.status === null;
    }),
      take(1),
      map( (accessRequest) => {
        if (accessRequest && (accessRequest.status === "PENDING")) {
            if (!isFirstTimePage && !isProfilePage) {
              // Redirects if not in 'first time setup' or 'personal profile' page
              this.router.carmNavigate([RoutesConst.AccountProfile_FirstTimeSetup]);
            }
        }
        // Always returns false since it's an orphan
        return false;
      },
      ));
  }

  /**
   * check Acls verification rules for request routes- if acl rules applied on route
   * @param userRolea
   * @param personalization
   * @returns true if verification
   */
  aclVerification(personalization: IAcl[]): boolean {
    if ( personalization && personalization.length > 0 ) {
      return this.accessControlService.isAclAllowedResource(personalization);
    } else {
      return true;
    }
  }

}
